import { create } from 'zustand'
import { apiService } from '@/services/api'
import apiCallManager from '../utils/apiCallManager'

const isNoActiveProgramError = (message) =>
  typeof message === 'string' && message.toLowerCase().includes('program details not found')

const useDashboardStore = create((set, get) => ({
  // State
  dashboardData: null,
  isLoading: false,
  error: null,
  lastFetchDate: null,

  // Actions
  fetchDashboardData: async (companyUrl, date = null) => {
    const { isLoading, dashboardData, lastFetchDate } = get()
    
    // Format date as YYYY-M-D (no leading zeros for month/day)
    const logdate = date ? formatDateForAPI(date) : formatDateForAPI(new Date())
    
    // Prevent duplicate calls if already loading for the same date
    if (isLoading && lastFetchDate === logdate) {
      return { success: true, data: dashboardData }
    }
    
    // Clear existing data if date has changed
    if (lastFetchDate && lastFetchDate !== logdate) {
      set({ dashboardData: null })
    }
    
    const requestKey = `fetchDashboardData:${companyUrl}:${logdate}`
    
    try {
      const result = await apiCallManager.executeRequest(
        requestKey,
        async (signal) => {
          return await apiService.fetchClientDashboard(companyUrl, logdate, null, signal)
        },
        {
          onStart: () => set({ isLoading: true, error: null }),
          onComplete: (response) => {
            if (response.success) {
              set({
                dashboardData: response.data,
                isLoading: false,
                lastFetchDate: logdate,
                error: null
              })
            } else if (isNoActiveProgramError(response.error)) {
              // No active program for the selected date isn't a real error — show the empty dashboard state instead.
              set({
                dashboardData: {},
                isLoading: false,
                lastFetchDate: logdate,
                error: null
              })
            } else {
              console.error('❌ Dashboard data fetch failed:', response.error)
              set({
                error: response.error,
                isLoading: false
              })
            }
          },
          onError: (error) => {
            if (error.message !== 'Request cancelled') {
              const errorMessage = error.message || 'Failed to fetch dashboard data'
              console.error('💥 Dashboard data fetch error:', error)
              set({ 
                error: errorMessage,
                isLoading: false 
              })
            }
          }
        }
      )
      return result
    } catch (error) {
      if (error.message !== 'Request cancelled') {
        const errorMessage = error.message || 'Failed to fetch dashboard data'
        console.error('💥 Dashboard data fetch error:', error)
        set({ 
          error: errorMessage,
          isLoading: false 
        })
        return { success: false, error: errorMessage }
      }
    }
  },

  // Clear dashboard data
  clearDashboardData: () => {
    set({
      dashboardData: null,
      error: null,
      lastFetchDate: null,
      isLoading: false
    })
  },

  // Refresh dashboard data
  refreshDashboardData: async (companyUrl) => {
    const { lastFetchDate } = get()
    const date = lastFetchDate ? new Date(lastFetchDate) : new Date()
    return await get().fetchDashboardData(companyUrl, date)
  }
}))

// Helper function to format date as YYYY-M-D
const formatDateForAPI = (date) => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = d.getMonth() + 1 // getMonth() returns 0-11
  const day = d.getDate()
  return `${year}-${month}-${day}`
}

export { useDashboardStore }